export const LAST_USER_TOKEN_COOKIE = 'lu';

export type ValidAuthTypeKey = 'Basic-Cloud' | 'Basic-AD' | 'Basic-External';

/** Auth types accepted by Mimecast-as-IdP SSO broker login (`loginSso`). */
export type SsoAuthTypeKey = ValidAuthTypeKey | 'SAML';

export type MfaType = 'totp' | 'email' | 'sms';

/** Credentials retained only for the email/SMS resend action. */
export interface MfaCredentials {
  username: string;
  password: string;
  authenticationType: SsoAuthTypeKey;
}

export interface MfaChallenge {
  mfaType: MfaType;
  destination: string | null;
  sessionId?: string | null;
  deliveryValid?: boolean;
  credentials?: MfaCredentials | null;
}

export interface AuthenticationType {
  key: ValidAuthTypeKey;
  selected?: boolean;
}

export interface DiscoverAuthenticationRegion {
  code: string;
  api: string;
}

export interface DiscoverAuthenticationData {
  authenticate?: string[];
  emailAddress?: string;
  emailToken?: string;
  lastAuthType?: string;
  region?: DiscoverAuthenticationRegion;
}

export type DiscoverAuthenticationInput =
  | { emailAddress: string }
  | { emailToken: string }
  | { lastUserToken: string };

export type DiscoverAuthenticationRequest =
  | { emailAddress: string; customerToken?: string }
  | { emailToken: string }
  | { lastUserToken: string };

export interface DiscoverAuthenticationResponse {
  emailAddress: string;
  authenticationTypes: AuthenticationType[];
  /**
   * Present when at least one Basic-* type is offered. Absent for SAML-only
   * accounts (password step is never shown).
   */
  selectedAuthenticationType?: AuthenticationType;
  /** True when discover returned SAML - UI initiates IdP redirect, not password. */
  hasSaml: boolean;
  /**
   * True when discover returned OIDC. UI may offer Continue with Microsoft when
   * the rollout flag is enabled; does not auto-redirect like SAML.
   */
  hasOidc: boolean;
  isExternal: boolean;
  emailToken?: string;
  region?: DiscoverAuthenticationRegion;
}

export interface LoginRequest {
  username: string;
  password: string;
  authenticationType: ValidAuthTypeKey;
  customerToken?: string;
  extendOnValidate: true;
  tokenType: 'key';
}

export interface LoginResponseData {
  sessionId?: string;
  username?: string;
  lastUserToken?: string;
  remoteAccountCode?: string;
}

export interface SubmitVerificationCodeRequest {
  code: string;
}

export interface SubmitVerificationCodeResponse {
  sessionId?: string;
  username?: string;
  lastUserToken?: string;
  remoteAccountCode?: string;
}

export interface RegisterOtpRequest {
  token: string;
}

export interface OtpRegistrationData {
  data: string; // base64 JPEG for the QR image
  secret: string; // plain-text TOTP secret
}

/** Legacy enrollSms payload: national number + uppercase ISO2 country code. */
export interface EnrollSmsRequest {
  username: string;
  smsNumber: {
    number: string;
    countryCode: string;
  };
}

export interface EnrollSmsResponse {
  sessionId?: string;
  username?: string;
  lastUserToken?: string;
  remoteAccountCode?: string;
}

export const LOGIN_ERROR_CODES = {
  PASSWORD_EXPIRED: 'err_xdk_password_expired',
  VERIFICATION_CODE_SENT: 'err_xdk_verification_code_sent',
  TOO_MANY_ATTEMPTS: 'err_xdk_too_many_attempts',
  INVALID_VERIFICATION_CODE: 'err_xdk_invalid_verification_code',
  SUBMIT_VERIFICATION_CODE: 'err_xdk_submit_verification_code',
  INVALID_SMS_NUMBER: 'err_xdk_invalid_sms_number',
  XDK_FAILURE: 'xdk_failure',
  AUTHENTICATE_WITH_IDENTITY_PROVIDER:
    'err_xdk_authenticate_with_identity_provider',
} as const;

export interface SamlIdpRedirectRequest {
  username: string;
  customerToken?: string;
  appId?: string;
  extendOnValidate: true;
  tokenType: 'key';
  samlResultPostTarget: string;
}

export interface GetSamlResponseRequest {
  key: string;
}

export interface OidcIdpRedirectRequest {
  username: string;
  customerToken?: string;
  appId?: string;
  extendOnValidate: true;
  tokenType: 'key';
  oidcResultPostTarget: string;
}

export interface GetOidcResponseRequest {
  key: string;
}

export const MFA_ERROR_CODES = {
  INVALID_VERIFICATION_CODE: 'err_xdk_invalid_verification_code',
  TOO_MANY_ATTEMPTS: 'err_xdk_too_many_attempts',
  /**
   * Backend signal that the temporary MFA session is gone. Legacy falls through
   * `errorHandler` default (no dedicated UI). We map it to the generic
   * "return to login" copy via the default branch in MfaVerificationBase.
   */
  SESSION_EXPIRED: 'err_login_invalid_email_address_user_not_found',
} as const;

export const VERIFICATION_CODE_DELIVERY_METHOD = {
  EMAIL: 0,
  SMS: 1,
} as const;

export interface LoginErrorDetails {
  changePasswordToken?: string;
  otpToken?: string;
  verificationCodeDeliveryMethod?: number;
  verificationCodeDeliveryAddress?: string;
  /**
   * From the 401 MFA challenge body. Sent back as the `sid` header on MFA BFF
   * calls (`submit-verification-code`, SSO verify, enroll-sms).
   */
  sessionId?: string;
}

/**
 * Thrown by `AuthenticationService.login()` for both known `err_xdk_*` failures and
 * unrecognized/network/5xx failures (`code` is `''` in the latter case). Consumers
 * should switch on `code` and fall back to a generic error for anything unmatched -
 * matching legacy `errorHandler`'s `default` case / `showLoginError()`.
 */
export class LoginError extends Error {
  constructor(
    readonly code: string,
    readonly details: LoginErrorDetails = {}
  ) {
    super(code || 'login_failed');
    this.name = 'LoginError';
  }
}

/**
 * Thrown by `AuthenticationService.submitVerificationCode()` for known and
 * unrecognized MFA verification failures.
 */
export class MfaError extends Error {
  constructor(readonly code: string) {
    super(code || 'mfa_failed');
    this.name = 'MfaError';
  }
}

/** Notification channels returned by validate-captcha-response. */
export type PasswordResetNotificationType = 'email' | 'sms' | 'email_sms';

export interface PasswordRules {
  minPasswordLength?: number;
  passwordLowerChar?: boolean;
  passwordUpperChar?: boolean;
  passwordNumericChar?: boolean;
  passwordSpecialChar?: boolean;
  lowercaseRequired?: boolean;
  uppercaseRequired?: boolean;
  numericCharRequired?: boolean;
  specialCharRequired?: boolean;
}

export interface ResetPasswordRequestPayload {
  emailAddress: string;
}

export interface ResetPasswordRequestResponse {
  captcha: string;
  token: string;
  context?: string;
}

export interface ValidateCaptchaPayload {
  emailAddress: string;
  captcha: string;
  token: string;
}

export interface ValidateCaptchaResponse {
  notificationType: PasswordResetNotificationType;
  token: string;
}

export interface SendNotificationPayload {
  emailAddress: string;
  notificationType: 'email' | 'sms';
  token: string;
}

export interface SendNotificationResponse {
  token: string;
}

export interface SendConfirmationPayload {
  emailAddress: string;
  resetCode: string;
  token: string;
}

export interface SendConfirmationResponse {
  passwordRules: PasswordRules;
  token: string;
}

export interface ResetPasswordPayload {
  emailAddress: string;
  resetCode: string;
  newPassword: string;
  token: string;
}

export const PASSWORD_RESET_ERROR_CODES = {
  CAPTCHA: 'captcha_error',
  GENERIC: 'generic_error',
  RESET_CODE: 'reset_code_error',
  RESET_CODE_EXPIRED: 'err_login_password_reset_code_expired_failure',
} as const;

/**
 * Thrown by password-reset API methods for known and unrecognized failures.
 */
export class PasswordResetError extends Error {
  constructor(readonly code: string) {
    super(code || PASSWORD_RESET_ERROR_CODES.GENERIC);
    this.name = 'PasswordResetError';
  }
}

export const CHANGE_PASSWORD_ERROR_CODES = {
  INVALID_CURRENT_PASSWORD: 'err_xdk_invalid_current_password',
  WEAK_PASSWORD: 'err_xdk_weak_password',
  INVALID_TOKEN: 'err_xdk_invalid_token',
  PASSWORD_HISTORY: 'err_login_password_history',
} as const;

export interface PasswordRulesConfig {
  minPasswordLength: number;
  lowercaseRequired: boolean;
  uppercaseRequired: boolean;
  numericCharRequired: boolean;
  specialCharRequired: boolean;
}

export interface GetPasswordRulesRequest {
  changePasswordToken: string;
  emailAddress: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  emailAddress: string;
  changePasswordToken: string;
}

export interface ChangePasswordResponseData {
  emailAddress: string;
  status: string;
}

export class ChangePasswordError extends Error {
  constructor(readonly code: string) {
    super(code || 'change_password_failed');
    this.name = 'ChangePasswordError';
  }
}

/** Shared by the Secure Messaging forgot-password and expired-password journeys. */
export interface SecureMessagingResetPasswordPayload {
  emailAddress: string;
  isPasswordExpired: boolean;
  customerToken?: string;
}

export interface GetPasswordRulesPayload {
  forgotPasswordToken: string;
}

export interface SetSecureMessagingPasswordPayload {
  password: string;
  token: string;
}

/** Entry params for Mimecast-as-IdP SSO broker (`gta=sso`). */
export interface SsoParams {
  token: string;
  relaystate: string;
  context: string;
}

/**
 * Matches legacy MUL `loginSso`: JSON `relayState` (camelCase) only when the
 * URL had `relaystate`. `samlResultPostTarget` only on SAML-under-sso.
 * Empty `context` is omitted.
 */
export interface SsoLoginRequest {
  emailAddress: string;
  token: string;
  password: string;
  authenticationType: SsoAuthTypeKey;
  relayState?: string;
  samlResultPostTarget?: string;
  context?: string;
}

/** Matches legacy MUL `sendCode` SSO payload (`relayState` camelCase). */
export interface SsoSubmitVerificationCodeRequest {
  code: string;
  token: string;
  relayState: string;
  context: string;
}

export interface SsoSamlRedirectRequest {
  emailAddress: string;
  token: string;
  relayState?: string;
  context?: string;
}

export interface SsoSamlRedirectResponse {
  redirectUrl: string;
  samlResponse: string;
  relayState: string;
}

/**
 * Thrown by `AuthenticationService.getSAMLRedirectSso()` for known and
 * unrecognized SSO handoff failures.
 */
export class SsoError extends Error {
  constructor(readonly code: string) {
    super(code || 'sso_failed');
    this.name = 'SsoError';
  }
}
